import { useQuery } from "@tanstack/react-query";
import { ErmineQueryKey } from "../utils/reactQueryKeyUtils";
import { getPortalConfig } from "../configurations";
import { AWSError } from "aws-sdk";
import {
    GetTermsOfServiceRequest,
    GetTermsOfServiceResponse,
} from "@amzn/ermine-ssr-sdk/clients/ermineserversiderenderingservice";
import log, { METRIC_NAME } from "../logging";
import { ICredentials } from "@aws-amplify/core";
import { useGetErmineSsrClient } from "./useGetErmineSsrClient";

export const useGetTermsOfService = (
    portalArn: string,
    userSettingsId: string
) => {
    const { ermineService, isServiceReady } = useGetErmineSsrClient();

    const getTermsOfService = () =>
        new Promise<string>((resolve, reject) => {
            ermineService.makeUnauthenticatedRequest(
                "getTermsOfService",
                {
                    portalArn,
                } as GetTermsOfServiceRequest,
                (err: AWSError, data: GetTermsOfServiceResponse) => {
                    if (err) {
                        log.error(
                            `GetTermsOfService ${err.statusCode} | ${err.code}: ${err.message} (requestID: ${err.requestId})`
                        );
                        log.publishMetricWithDimensions(
                            METRIC_NAME.MISSING_CUSTOM_BRANDING_ASSET,
                            {
                                resource: "TermsOfService",
                            }
                        );
                        reject(err);
                    } else {
                        log.publishMetricWithDimensions(
                            METRIC_NAME.CUSTOM_BRANDING_ASSET_SUCCESS,
                            {
                                resource: "TermsOfService",
                            }
                        );
                        resolve(data.termsOfService);
                    }
                }
            );
        });

    const { data, isLoading, error, refetch, isError } = useQuery({
        queryKey: ErmineQueryKey.GetTermsOfService(portalArn, userSettingsId),
        queryFn: () => getTermsOfService(),
        enabled: !!portalArn && !!userSettingsId && isServiceReady,
        staleTime: Infinity,
    });

    return {
        content: data,
        isLoading,
        error: error as AWSError | undefined,
        refetch,
        isError,
    };
};
